import React, { useEffect, useRef, useState } from "react";

// Legacy keys kept so the existing section components keep compiling.
// Visual look now comes from the stylesheet below (wb-legal__*).
const styles = {
  container: { display: "contents" },
  sectionDivider: { display: "none" },
  sectionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "5px 11px",
    borderRadius: "999px",
    background: "rgba(58,110,212,0.08)",
    border: "1px solid rgba(58,110,212,0.22)",
    color: "#3A6ED4",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    width: "fit-content",
    margin: "56px 0 12px",
  },
  sectionTitle: {
    fontSize: "clamp(22px, 2.6vw, 30px)",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    margin: "0 0 8px",
    color: "#0B1020",
    paddingBottom: "16px",
    borderBottom: "1px solid #E6EAF2",
    scrollMarginTop: "96px",
  },
  heading: {
    fontSize: "clamp(17px, 1.6vw, 19px)",
    fontWeight: 700,
    letterSpacing: "-0.005em",
    color: "#0B1020",
    marginTop: "36px",
    marginBottom: "12px",
    scrollMarginTop: "96px",
  },
  paragraph: {
    margin: "0 0 14px",
    color: "#2B3346",
    lineHeight: 1.75,
    fontSize: "15.5px",
  },
  backBtn: { display: "none" },
};

const LEGAL_CSS = `
.wb-legal {
  --wb-bg: #FFFFFF;
  --wb-ink: #0B1020;
  --wb-mute: #5A6480;
  --wb-line: #E6EAF2;
  --wb-soft: #F7F9FC;
  --wb-brand: #3A6ED4;
  --wb-brand-2: #5BC3BE;
  background: var(--wb-bg);
  color: var(--wb-ink);
  font-family: 'Poppins', sans-serif;
  padding-bottom: 96px;
}
.wb-legal * { box-sizing: border-box; }

/* Hero */
.wb-legal__hero {
  position: relative;
  overflow: hidden;
  padding: 96px 40px 72px;
  background: linear-gradient(180deg, #F2F6FF 0%, #FFFFFF 100%);
  border-bottom: 1px solid var(--wb-line);
  isolation: isolate;
}
.wb-legal__hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: -1;
}
.wb-legal__hero-orb--a {
  width: 420px; height: 420px;
  top: -140px; left: -120px;
  background: radial-gradient(circle, rgba(58,110,212,0.22), transparent 70%);
}
.wb-legal__hero-orb--b {
  width: 360px; height: 360px;
  bottom: -120px; right: -80px;
  background: radial-gradient(circle, rgba(91,195,190,0.18), transparent 70%);
}
.wb-legal__hero-inner {
  max-width: 1040px; margin: 0 auto;
  display: flex; flex-direction: column; gap: 18px;
}
.wb-legal__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(58,110,212,0.08);
  border: 1px solid rgba(58,110,212,0.22);
  color: var(--wb-brand);
  font-size: 12px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  width: fit-content;
}
.wb-legal__eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--wb-brand-2);
  box-shadow: 0 0 0 4px rgba(91,195,190,0.18);
}
.wb-legal__title {
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  font-size: clamp(40px, 6vw, 64px);
  color: var(--wb-ink);
}
.wb-legal__lede {
  max-width: 720px;
  color: var(--wb-mute);
  line-height: 1.65;
  font-size: clamp(15px, 1.5vw, 18px);
  margin: 0;
}
.wb-legal__meta {
  display: flex; flex-wrap: wrap; gap: 10px;
  margin-top: 8px;
}
.wb-legal__meta-pill {
  padding: 7px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--wb-line);
  font-size: 12.5px;
  color: var(--wb-mute);
}
.wb-legal__meta-pill strong { color: var(--wb-ink); font-weight: 600; }

/* Body grid */
.wb-legal__wrap {
  max-width: 1240px;
  margin: 0 auto;
  padding: 64px 40px 0;
}
.wb-legal__grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 56px;
  align-items: start;
}

/* TOC */
.wb-legal__toc {
  position: sticky;
  top: 96px;
  align-self: start;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
}
.wb-legal__toc-inner { padding-right: 8px; }
.wb-legal__toc-title {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--wb-mute);
  margin-bottom: 14px;
  font-size: 11px;
  font-weight: 700;
}
.wb-legal__toc-group { margin-bottom: 20px; }
.wb-legal__toc-group-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--wb-ink);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px;
  padding: 0 14px;
  text-decoration: none;
  cursor: pointer;
}
.wb-legal__toc-group-label:hover { color: var(--wb-brand); }
.wb-legal__toc-list {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 2px;
  border-left: 1px solid var(--wb-line);
}
.wb-legal__toc-link {
  display: flex; align-items: center; gap: 10px;
  padding: 7px 14px;
  margin-left: -1px;
  border-left: 2px solid transparent;
  font-size: 13px;
  color: var(--wb-mute);
  text-decoration: none;
  transition: color 0.15s, border-color 0.2s, background 0.15s;
}
.wb-legal__toc-link:hover { color: var(--wb-ink); background: var(--wb-soft); }
.wb-legal__toc-link--active {
  color: var(--wb-brand);
  border-left-color: var(--wb-brand);
  font-weight: 600;
}
.wb-legal__toc-marker {
  width: 4px; height: 4px; border-radius: 50%;
  background: currentColor; opacity: 0.6;
  flex-shrink: 0;
}

/* Article */
.wb-legal__article { max-width: 760px; min-width: 0; }
.wb-legal__article h2 { scroll-margin-top: 96px; }
.wb-legal__article h5 { scroll-margin-top: 96px; }
.wb-legal__article a {
  color: var(--wb-brand);
  text-decoration: none;
  border-bottom: 1px solid rgba(58,110,212,0.3);
  transition: border-color 0.15s;
}
.wb-legal__article a:hover { border-bottom-color: var(--wb-brand); }
.wb-legal__article strong { color: var(--wb-ink); font-weight: 600; }

/* Contact card */
.wb-legal__contact {
  margin-top: 64px;
  padding: 28px 32px;
  border-radius: 18px;
  background: linear-gradient(180deg, #F2F6FF, #FFFFFF);
  border: 1px solid var(--wb-line);
  font-size: 15px;
  color: var(--wb-ink);
  line-height: 1.6;
}
.wb-legal__contact a {
  color: var(--wb-brand);
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid rgba(58,110,212,0.3);
}
.wb-legal__contact a:hover { border-bottom-color: var(--wb-brand); }

/* Back to top */
.wb-legal__top {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 200;
  width: 46px; height: 46px;
  border-radius: 999px;
  background: #0B1020;
  color: #fff;
  border: none;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(11,16,32,0.25);
  opacity: 0; transform: translateY(8px);
  pointer-events: none;
  transition: opacity 0.25s, transform 0.25s, background 0.2s;
  font-size: 18px;
  font-family: inherit;
}
.wb-legal__top:hover { background: #1A2035; }
.wb-legal__top[data-show] {
  opacity: 1; transform: translateY(0);
  pointer-events: auto;
}

/* Responsive */
@media (max-width: 960px) {
  .wb-legal__hero { padding: 72px 24px 56px; }
  .wb-legal__wrap { padding: 40px 24px 0; }
  .wb-legal__grid { grid-template-columns: 1fr; gap: 32px; }
  .wb-legal__toc {
    position: static;
    order: -1;
    padding: 14px 16px;
    border-radius: 14px;
    background: var(--wb-soft);
    border: 1px solid var(--wb-line);
    max-height: none;
  }
  .wb-legal__toc-list { border-left: none; }
  .wb-legal__toc-link { border-left: none; padding: 6px 0; }
  .wb-legal__toc-link--active { background: transparent; }
  .wb-legal__toc-group-label { padding: 0; }
}
@media (max-width: 520px) {
  .wb-legal__hero { padding: 56px 20px 44px; }
  .wb-legal__wrap { padding: 32px 20px 0; }
  .wb-legal__contact { padding: 22px 20px; }
}
`;

const fmtDate = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return typeof v === "string" ? v : "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const slugify = (s, fallback) =>
  ((s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback);

const TermsOfUseSection = () => (
  <div>
    <p style={styles.sectionLabel}>Terms of Use</p>
    <h2 style={styles.sectionTitle}>General Terms</h2>

    <h5 style={styles.heading}>1. Introduction</h5>
    <p style={styles.paragraph}>
      These Terms of Use ("Terms") are a legally binding agreement between you
      and workbrook LLC or, where applicable, the workbrook affiliate identified
      in these Terms ("workbrook," "we," "us," or "our"), and govern your access
      to and use of the workbrook website, mobile applications, web application,
      software, recruiter portal, referral platform, communications, and related
      services (collectively, the "Services").
    </p>
    <p style={styles.paragraph}>
      By accessing or using the Services, creating an account, posting a job,
      making a referral, applying for a job, engaging a recruiter, purchasing a
      paid service, or otherwise interacting with the platform, you agree to be
      bound by these Terms and the workbrook Privacy Policy. If you do not agree,
      you must not use the Services.
    </p>
    <p style={styles.paragraph}>
      If you use the Services on behalf of a company, employer, recruitment
      agency, or other legal entity, you represent and warrant that you have
      authority to bind that entity to these Terms. In that case, "you" and
      "your" refer to both you and that entity.
    </p>
    <p style={styles.paragraph}>
      You must be at least 16 years old to use the Services, or older if the
      laws of your jurisdiction require a higher minimum age. The English version
      of these Terms controls to the extent any translated version conflicts with
      it.
    </p>

    <h5 style={styles.heading}>2. Definitions</h5>
    <p style={styles.paragraph}>
      For purposes of these Terms: "Applicant" means a user who applies for a
      job through the Services. "Candidate" means an individual who is
      identified, referred, sourced, applies, interviews, or is considered for a
      role through the Services. "Employer" or "Job Poster" means a user or
      organization that posts a job, advertises an opportunity, or uses the
      Services to hire talent. "Recruiter" means a user approved by workbrook to
      offer recruiting or talent acquisition services through the recruiter
      portal. "Referrer" means a user who shares a job opportunity through
      workbrook and refers a potential Applicant.
    </p>
    <p style={styles.paragraph}>
      "Referral" means a tracked submission, share, recommendation, or
      platform-recognized introduction of a Candidate to a job through
      workbrook. "Hire" means an Applicant or Candidate beginning paid
      employment, consulting, contract work, or other compensated engagement
      with an Employer or its affiliate. "Start Date" means the first day on
      which the hired Candidate begins paid work in the applicable role.
      "Wallet" means a prepaid balance, employer credit balance, promotional
      credit balance, referral reward balance, or similar stored-value mechanism
      within the Services.
    </p>

    <h5 style={styles.heading}>3. Eligibility, Registration, and Accounts</h5>
    <p style={styles.paragraph}>
      To use certain features of the Services, you must create an account and
      provide accurate, current, and complete information. You agree to keep your
      information updated at all times and to maintain a valid email address that
      you own and control.
    </p>
    <p style={styles.paragraph}>
      You are responsible for maintaining the confidentiality of your account
      credentials and for all activity that occurs under your account. You must
      notify workbrook promptly if you believe your account or security
      credentials have been compromised or used without authorization.
    </p>
    <p style={styles.paragraph}>
      workbrook may permit sign-in through third-party services or social
      networking platforms. If you use such a login method, you authorize
      workbrook to receive information from that third party in accordance with
      your settings and permissions on that service.
    </p>
    <p style={styles.paragraph}>
      workbrook may reject, suspend, restrict, or terminate any account where we
      reasonably believe the account information is false, incomplete,
      unauthorized, duplicative, fraudulent, abusive, or otherwise inconsistent
      with these Terms.
    </p>

    <h5 style={styles.heading}>4. Use on Behalf of an Organization</h5>
    <p style={styles.paragraph}>
      If you use the Services on behalf of an employer, company, recruitment
      agency, or other legal entity, you represent and warrant that you are duly
      authorized to act on behalf of that entity, that the entity agrees to be
      bound by these Terms, and that you will use the Services only within the
      scope of your authority.
    </p>
    <p style={styles.paragraph}>
      Organizations are responsible for activity conducted through accounts they
      authorize or administer. If you falsely claim authority to act on behalf of
      an entity, workbrook may suspend or terminate your account and pursue any
      remedies available under law or contract.
    </p>

    <h5 style={styles.heading}>5. Intellectual Property and Limited License</h5>
    <p style={styles.paragraph}>
      The Services, including all software, design, layout, text, graphics,
      logos, trademarks, interface elements, platform features, databases, and
      other content made available by workbrook, are owned by or licensed to
      workbrook and are protected by intellectual property and other applicable
      laws.
    </p>
    <p style={styles.paragraph}>
      Subject to your compliance with these Terms, workbrook grants you a
      limited, revocable, non-exclusive, non-transferable, non-sublicensable
      license to access and use the Services for their intended purposes.
    </p>
    <p style={styles.paragraph}>
      Except as expressly permitted by workbrook in writing, you may not copy,
      reproduce, republish, distribute, sell, license, rent, lease, transfer, or
      commercially exploit the Services or workbrook content; modify, adapt,
      translate, create derivative works from, decompile, reverse engineer, or
      disassemble the Services; scrape, extract, harvest, index, or use data
      from the Services for dataset creation, model training, or any
      unauthorized commercial purpose; or remove, alter, or obscure any
      copyright, trademark, or proprietary notices.
    </p>

    <h5 style={styles.heading}>6. User Content and License to workbrook</h5>
    <p style={styles.paragraph}>
      You retain ownership of content, information, resumes, profiles, job
      descriptions, images, messages, application materials, and other materials
      that you upload, post, submit, transmit, or otherwise make available
      through the Services ("User Content"), subject to the rights you grant in
      these Terms.
    </p>
    <p style={styles.paragraph}>
      By submitting User Content through the Services, you grant workbrook a
      non-exclusive, worldwide, royalty-free license to host, store, reproduce,
      process, format, display, distribute, transmit, analyze, adapt, and use
      that User Content only as reasonably necessary to operate, provide, secure,
      maintain, improve, and support the Services; facilitate hiring, referrals,
      recruiting, communication, and related platform functions; generate
      analytics in aggregated or de-identified form; enforce these Terms; and
      comply with legal obligations.
    </p>
    <p style={styles.paragraph}>
      You represent and warrant that you own or control all rights necessary to
      submit the User Content, that the User Content does not violate law or
      infringe the rights of any third party, and that you have any required
      consents, permissions, or lawful basis to share personal information
      contained in the User Content.
    </p>

    <h5 style={styles.heading}>7. Acceptable Use</h5>
    <p style={styles.paragraph}>
      You agree to use the Services only for lawful, authorized, and intended
      purposes. You are solely responsible for your use of the Services, your
      account, your User Content, and your interactions with other users.
    </p>
    <p style={styles.paragraph}>
      You may not upload, post, send, transmit, or otherwise make available
      content or information that is unlawful, fraudulent, deceptive, misleading,
      abusive, threatening, defamatory, discriminatory, obscene, sexually
      explicit, infringing, or otherwise harmful. You may not use the Services in
      a way that violates privacy rights, confidentiality obligations, anti-spam
      laws, employment laws, anti-discrimination laws, data protection laws,
      intellectual property rights, or any other applicable law.
    </p>

    <h5 style={styles.heading}>8. Prohibited Activities</h5>
    <p style={styles.paragraph}>
      You agree not to create false accounts, duplicate accounts, or accounts
      under false pretenses; impersonate any person or entity, or misrepresent
      your identity, authority, qualifications, work history, or affiliation;
      post fake jobs, fake candidates, fake applications, fake referrals, or
      misleading hiring activity; or collude with another user, Employer,
      Applicant, Recruiter, or Candidate to manipulate payouts, rankings, Match
      Scores, billing events, or platform outcomes.
    </p>
    <p style={styles.paragraph}>
      You further agree not to use bots, scripts, crawlers, spiders, scrapers,
      data-mining tools, or other automated means to access or interact with the
      Services without workbrook's written authorization; attempt to bypass
      security, authentication, access controls, or encryption; upload viruses,
      malware, or other harmful code; interfere with or disrupt the Services;
      access non-public areas of the Services; or use platform data or output to
      train artificial intelligence or machine learning systems without
      authorization.
    </p>

    <h5 style={styles.heading}>9. Applicant Terms</h5>
    <p style={styles.paragraph}>
      Applicants may apply to jobs through the Services using their workbrook
      accounts, subject to job eligibility requirements and these Terms.
    </p>
    <p style={styles.paragraph}>
      By applying to a job through workbrook, you authorize workbrook to transmit
      your application materials, profile information, resume, and related
      information to the relevant Employer, Job Poster, Recruiter, or other
      authorized hiring participant involved in the opportunity; represent and
      warrant that the information you provide is truthful, current, and not
      misleading; and acknowledge that Employers, not workbrook, make hiring
      decisions.
    </p>
    <p style={styles.paragraph}>
      Applicants may withdraw from a job application through available platform
      tools or by otherwise notifying the relevant party, subject to ongoing
      legal, compliance, or record-retention requirements.
    </p>

    <h5 style={styles.heading}>10. Recruiter Terms</h5>
    <p style={styles.paragraph}>
      To act as a Recruiter on workbrook, you must create a recruiter profile and
      satisfy workbrook's eligibility requirements, including any experience,
      screening, approval, or profile-completion requirements communicated by
      workbrook.
    </p>
    <p style={styles.paragraph}>
      Recruiters must use the Services only for legitimate and lawful recruiting
      purposes and remain subject to these Terms. Where workbrook provides a
      separate Recruiter Agreement or service-specific contract, that agreement
      supplements these Terms and controls in the event of conflict as to
      Recruiter services.
    </p>

    <h5 style={styles.heading}>11. Privacy, Communications, and Data Protection</h5>
    <p style={styles.paragraph}>
      Your use of the Services is subject to workbrook's Privacy Policy, which
      describes how workbrook collects, uses, stores, discloses, and otherwise
      processes personal data.
    </p>
    <p style={styles.paragraph}>
      You agree not to upload, share, transmit, or otherwise disclose personal
      information through the Services unless you have the legal right or lawful
      basis to do so. Employers, Recruiters, and Referrers are independently
      responsible for their own lawful handling of personal information they
      receive through the Services.
    </p>
    <p style={styles.paragraph}>
      workbrook may send service-related, transactional, operational, security,
      billing, legal, and support communications in connection with the Services.
      Marketing communications are subject to applicable law and any preferences
      or consents required by law.
    </p>

    <h5 style={styles.heading}>12. AI and Automated Features</h5>
    <p style={styles.paragraph}>
      workbrook may use artificial intelligence, machine learning, automation,
      algorithmic ranking, predictive systems, or similar technologies in
      connection with the Services, including to summarize job descriptions or
      candidate profiles, calculate Match Scores or relevance signals, rank or
      prioritize candidates or jobs, detect suspicious activity or fraud,
      recommend opportunities or candidates, and support outreach or workflow
      automation.
    </p>
    <p style={styles.paragraph}>
      These outputs are assistive tools only. They may be incomplete, inaccurate,
      probabilistic, or based on limited inputs. They do not constitute legal,
      HR, employment, or professional advice, and they do not replace human
      judgment. Users remain solely responsible for decisions they make using the
      Services.
    </p>

    <h5 style={styles.heading}>13. Service Management and Enforcement</h5>
    <p style={styles.paragraph}>
      workbrook reserves the right, but not the obligation, to monitor the
      Services for compliance with these Terms; investigate suspected violations,
      fraud, abuse, billing disputes, and suspicious activity; remove, disable,
      restrict, or refuse access to content, jobs, features, accounts, Wallets,
      or Services; and preserve, review, and disclose information as reasonably
      necessary to enforce these Terms, comply with law, or protect rights,
      property, and safety.
    </p>
    <p style={styles.paragraph}>
      workbrook's records, logs, timestamps, and platform data may be used in
      determining attribution, billing, fraud, reward eligibility, and compliance
      outcomes.
    </p>

    <h5 style={styles.heading}>14. Suspension and Termination</h5>
    <p style={styles.paragraph}>
      workbrook may suspend, limit, or terminate your access to all or part of
      the Services immediately where reasonably necessary due to suspected fraud,
      abuse, manipulation, security risk, unlawful conduct, nonpayment,
      violation of these Terms, false or misleading account or transaction
      information, or legal or reputational risk to workbrook or the Services.
    </p>
    <p style={styles.paragraph}>
      Where reasonably practicable, workbrook may provide notice of suspension or
      termination, but workbrook is not required to do so in advance where urgent
      action is needed.
    </p>
    <p style={styles.paragraph}>
      Account deletion or discontinuation does not relieve you of obligations
      accrued before termination, including payment obligations, tax obligations,
      indemnity obligations, confidentiality obligations, or disputes already in
      progress.
    </p>

    <h5 style={styles.heading}>15. Disclaimers</h5>
    <p style={styles.paragraph}>
      THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE
      MAXIMUM EXTENT PERMITTED BY LAW, WORKBROOK DISCLAIMS ALL WARRANTIES,
      WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING IMPLIED
      WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
      NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR
      USAGE OF TRADE.
    </p>
    <p style={styles.paragraph}>
      workbrook does not warrant that the Services will be uninterrupted,
      error-free, secure, or always available; that any job posting, referral,
      applicant profile, resume, recruiter profile, company information, salary
      information, Match Score, or platform content is accurate, complete,
      reliable, or lawful; or that any hire, interview, application, reward, or
      commercial outcome will occur.
    </p>
    <p style={styles.paragraph}>
      Users are solely responsible for conducting their own due diligence,
      screening, interviews, reference checks, background checks, legal
      compliance, and business judgment.
    </p>

    <h5 style={styles.heading}>16. Limitation of Liability</h5>
    <p style={styles.paragraph}>
      To the maximum extent permitted by law, workbrook and its affiliates,
      officers, directors, employees, contractors, licensors, and agents will not
      be liable for any indirect, incidental, consequential, special, exemplary,
      or punitive damages, including loss of profits, loss of revenue, loss of
      opportunity, loss of data, business interruption, reputational harm, or
      procurement of substitute services, arising out of or relating to the
      Services or these Terms, even if advised of the possibility of such
      damages.
    </p>
    <p style={styles.paragraph}>
      To the maximum extent permitted by law, workbrook's aggregate liability for
      all claims arising out of or relating to the Services or these Terms will
      not exceed the greater of the amount you paid to workbrook in the 12 months
      preceding the event giving rise to the claim or USD 100. Nothing in these
      Terms excludes liability that cannot lawfully be excluded or limited.
    </p>

    <h5 style={styles.heading}>17. Indemnification</h5>
    <p style={styles.paragraph}>
      You agree to defend, indemnify, and hold harmless workbrook, its
      affiliates, and their respective officers, directors, employees,
      contractors, licensors, and agents from and against any claims,
      liabilities, damages, judgments, losses, costs, and expenses, including
      reasonable attorneys' fees, arising out of or related to your use of the
      Services, your violation of these Terms, your User Content, your violation
      of any law or the rights of a third party, your misuse of personal
      information or candidate data, or any fraud, misrepresentation, spam,
      discriminatory hiring activity, fake postings, fake referrals, or other
      prohibited conduct by you.
    </p>

    <h5 style={styles.heading}>18. Modifications, Interruptions, and Service Changes</h5>
    <p style={styles.paragraph}>
      workbrook may modify, suspend, discontinue, or update any aspect of the
      Services at any time, including features, pricing, tools, algorithms,
      integrations, tiers, reward structures, or availability. workbrook is not
      liable for any unavailability, downtime, interruption, maintenance event,
      feature withdrawal, pricing change, or service change, except as required
      by law or expressly agreed in writing.
    </p>
    <p style={styles.paragraph}>
      workbrook may make changes to these Terms from time to time. If we make
      material changes, we may provide notice through the Services, by email, or
      by other reasonable means. Your continued use of the Services after the
      effective date of updated Terms constitutes acceptance of the updated Terms.
    </p>
    <p style={styles.paragraph}>
      Beta Features. workbrook may designate certain features, tools, workflows,
      integrations, AI functions, dashboards, or services as beta, pilot,
      early-access, preview, or test features ("Beta Features"). Beta Features
      may be incomplete, unstable, unavailable, or subject to errors,
      interruptions, data loss, or material change. workbrook may modify,
      suspend, or discontinue Beta Features at any time, with or without notice,
      and has no obligation to support, maintain, correct, or continue Beta
      Features. Beta Features are provided on an "as is" and "as available"
      basis, and users use them at their own risk.
    </p>

    <h5 style={styles.heading}>19. Dispute Resolution and Governing Law</h5>
    <p style={styles.paragraph}>
      These Terms are governed by the laws of the State of Texas, without regard
      to conflict of laws principles. Before filing a formal legal claim, each
      party agrees to first attempt to resolve the dispute informally by written
      notice to the other party describing the nature of the dispute and the
      requested relief.
    </p>
    <p style={styles.paragraph}>
      If the dispute is not resolved informally within 30 days, either party may
      require the dispute to proceed to mediation in Texas, unless emergency
      injunctive relief is needed. If mediation does not resolve the dispute,
      then, unless applicable law requires otherwise, the dispute shall be
      brought exclusively in the state or federal courts located in Texas, and
      each party consents to the personal jurisdiction and venue of those courts.
    </p>
    <p style={styles.paragraph}>
      Class Action and Jury Trial Waiver: You and workbrook agree that any
      dispute resolution proceedings will be conducted only on an individual
      basis and not in a class, consolidated, or representative action. If for
      any reason a claim proceeds in court rather than in arbitration or
      mediation, you and workbrook each waive any right to a jury trial.
    </p>

    <h5 style={styles.heading}>20. Miscellaneous</h5>
    <p style={styles.paragraph}>
      These Terms, together with any policies, pricing pages, order forms,
      service-specific agreements, and other terms expressly incorporated by
      reference, constitute the entire agreement between you and workbrook
      regarding the Services, except where a separate written agreement governs a
      specific service.
    </p>
    <p style={styles.paragraph}>
      If any provision of these Terms is found unenforceable, the remaining
      provisions will remain in full force and effect. workbrook's failure to
      enforce any provision is not a waiver of its right to do so later. You may
      not assign or transfer these Terms without workbrook's prior written
      consent. workbrook may assign these Terms in connection with a merger,
      acquisition, reorganization, sale of assets, or by operation of law.
    </p>
    <p style={styles.paragraph}>
      For legal notices or questions regarding these Terms, contact: info@workbrook.com.
    </p>
  </div>
);

const EmployerTermsSection = () => (
  <div>
    <hr style={styles.sectionDivider} />
    <p style={styles.sectionLabel}>Employer / Marketplace</p>
    <h2 style={styles.sectionTitle}>Employer Commercial Terms</h2>

    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <p style={styles.paragraph}>
      These Employer / Marketplace Commercial Terms ("Employer Commercial Terms")
      govern an Employer's use of workbrook's paid employer-facing marketplace
      and hiring services, including tiered job promotion, Wallet funding,
      usage-based billing, placement services, guarantees, and related commercial
      mechanics.
    </p>
    <p style={styles.paragraph}>
      These Employer Commercial Terms are incorporated into and form part of the
      workbrook Terms of Use. In the event of conflict, these Employer Commercial
      Terms control for the commercial subjects they expressly govern. Separate
      signed proposals, order forms, search agreements, or client agreements
      control to the extent they expressly override these Employer Commercial
      Terms.
    </p>

    <h5 style={styles.heading}>2. Employer Eligibility and Authority</h5>
    <p style={styles.paragraph}>
      By posting a job or purchasing any paid employer service, you represent and
      warrant that the job is real, lawful, and currently open or reasonably
      anticipated to open; that you are authorized to recruit for the role; that
      the job description, company information, salary information, and hiring
      details are accurate in all material respects; and that your use of
      workbrook complies with applicable employment, anti-discrimination, and
      privacy laws.
    </p>
    <p style={styles.paragraph}>
      You may not post speculative, misleading, already-filled, unlawful,
      discriminatory, or confidential jobs.
    </p>

    <h5 style={styles.heading}>3. Employer Conduct and Reporting Obligations</h5>
    <p style={styles.paragraph}>
      Employers must keep job postings current and accurate; promptly mark roles
      as filled, paused, canceled, or closed; provide materially accurate
      information about job requirements, compensation, location, reporting
      structure, and work authorization requirements; use Candidate information
      only for legitimate hiring-related purposes; and comply with all applicable
      laws in reviewing, contacting, interviewing, and hiring Candidates.
    </p>
    <p style={styles.paragraph}>
      Employers must also provide reasonably timely and truthful updates
      regarding interviews, offers, acceptances, Start Dates, compensation, and
      terminations where required for billing, referral rewards, replacement
      guarantees, audit, or dispute resolution.
    </p>

    <h5 style={styles.heading}>4. Candidate Attribution and Ownership</h5>
    <p style={styles.paragraph}>
      A Candidate is considered introduced by workbrook when workbrook, a
      Referrer through workbrook, a Recruiter through workbrook, or any
      workbrook-supported process first provides or tracks the Candidate in
      connection with a role.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise agreed in writing, workbrook's attribution period for
      introduced or referred Candidates is 12 months from the most recent
      material workbrook-tracked activity involving that Candidate and the
      Employer. If an Employer hires, engages, or causes a Candidate to be hired
      or engaged directly or indirectly during the attribution period, workbrook
      may assess the applicable fee or treat the hire as reward-eligible under
      the applicable workbrook terms.
    </p>
    <p style={styles.paragraph}>
      For the purposes of this Section, 'material workbrook-tracked activity' is
      strictly defined as one or more of the following verifiable platform
      events: (a) a formal job application submitted by the Candidate, (b) a
      documented interview scheduled or facilitated via the Services, or (c)
      direct, mutual written correspondence between the Employer and the
      Candidate initiated through the platform. This clause is associated with
      Tier 3 and/or Recruiter placement services.
    </p>

    <h5 style={styles.heading}>5. Tier 1 — Direct-to-Talent</h5>
    <p style={styles.paragraph}>
      Tier 1 provides standard visibility in the workbrook job feed and may
      include basic posting functionality. Unless workbrook states otherwise,
      Tier 1 does not include premium ranking, white-glove search, AI-assisted
      outreach, or pay-per-view referral amplification.
    </p>
    <p style={styles.paragraph}>
      workbrook may impose fair-use limits on free job postings, active jobs,
      editing frequency, refreshes, visibility boosts, or other free-tier
      features.
    </p>

    <h5 style={styles.heading}>6. Tier 2 — Community Boosted</h5>
    <p style={styles.paragraph}>
      Tier 2 is a usage-based service in which an Employer funds a Wallet and is
      charged a stated View Rate or other defined usage rate for qualifying
      access events generated through referral or premium discovery mechanisms.
    </p>
    <p style={styles.paragraph}>
      A billable View is determined by workbrook's tracking systems and may
      exclude duplicate views by the same user within a defined period, bot
      traffic, fraudulent or invalid activity, accidental or incomplete loads,
      and any event workbrook determines should not be charged in its discretion.
    </p>
    <p style={styles.paragraph}>
      workbrook may allow Employers to set daily or total spend caps. Once a cap
      or available Wallet balance is exhausted, workbrook may pause, reduce, or
      revert the job's premium exposure until funding is restored or settings are
      changed. Except where required by law or expressly stated by workbrook,
      valid usage charges are non-refundable once incurred.
    </p>

    <h5 style={styles.heading}>7. Tier 3 — Full Placement / Performance-Based Search</h5>
    <p style={styles.paragraph}>
      Tier 3 is a premium, performance-based hiring service that may include
      targeted sourcing, referral bounty activation, AI-assisted talent discovery
      or passive outreach, priority screening or ranking, and white-glove
      support.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise agreed in writing, activation of Tier 3 may require a
      non-refundable retainer. The remaining fee structure, placement
      percentages, role bands, and invoicing triggers are as stated in the
      applicable order form, proposal, pricing page, or service-specific
      agreement.
    </p>
    <p style={styles.paragraph}>
      If Tier 3 is governed by a separate client agreement or search agreement,
      that agreement controls to the extent of any conflict.
    </p>

    <h5 style={styles.heading}>8. Replacement Guarantee</h5>
    <p style={styles.paragraph}>
      Where workbrook offers a replacement guarantee for an applicable paid
      search or placement service, workbrook may conduct one replacement search
      at no additional professional fee if the placed Candidate leaves or is
      terminated for cause during the Guarantee Period, provided that the
      Employer notifies workbrook in writing within the required notice window;
      the termination did not result from redundancy, material role change,
      compensation change, relocation, restructuring, or employer misconduct;
      all applicable fees have been paid in full; and the Employer otherwise
      remains in compliance with the workbrook documents.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise stated, replacement guarantees do not automatically
      include new referral bounties, paid advertising costs, or third-party
      expenses.
    </p>

    <h5 style={styles.heading}>9. Wallets and Credits</h5>
    <p style={styles.paragraph}>
      workbrook may offer Wallet functionality for prepaid balances, purchased
      credits, referral rewards, promotional credits, or similar value units.
      Unless workbrook states otherwise, purchased Wallet balances are
      non-interest-bearing, promotional credits have no cash value and may expire
      or be withdrawn, Wallet balances are non-transferable except as expressly
      permitted by workbrook, and workbrook may distinguish between purchased
      credits and promotional credits for redemption order, refund treatment, or
      expiration.
    </p>
    <p style={styles.paragraph}>
      Employers are responsible for maintaining sufficient Wallet balance where a
      service requires prepaid funding.
    </p>

    <h5 style={styles.heading}>10. Billing, Invoicing, and Payment</h5>
    <p style={styles.paragraph}>
      You authorize workbrook and its payment processors to charge your selected
      payment method for fees you incur. You must provide accurate billing, tax,
      and payment information and keep that information current. You are
      responsible for any fees charged by your payment provider, bank, card
      issuer, or payment processor.
    </p>
    <p style={styles.paragraph}>
      Except where required by law or expressly stated by workbrook in writing,
      fees already incurred for valid Services, usage events, retainers, consumed
      credits, subscriptions already billed, and completed service milestones are
      non-refundable.
    </p>
    <p style={styles.paragraph}>
      If a payment fails, is reversed, is charged back, or is disputed,
      workbrook may suspend your access to paid Services, recover or offset
      amounts owed, invalidate related rewards or promotional benefits where
      appropriate, and pursue collection costs and legal remedies.
    </p>

    <h5 style={styles.heading}>11. Taxes</h5>
    <p style={styles.paragraph}>
      Employers are responsible for all taxes, duties, levies, withholdings, and
      other governmental charges arising from their use of paid Services, except
      taxes imposed on workbrook's net income. workbrook may withhold amounts
      where required by law.
    </p>

    <h5 style={styles.heading}>12. Anti-Circumvention</h5>
    <p style={styles.paragraph}>
      Employers may not bypass workbrook to avoid applicable fees, billing rules,
      or referral reward obligations. Circumvention includes hiring or engaging a
      workbrook-introduced or workbrook-referred Candidate off-platform to avoid
      fees; requesting that Applicants, Candidates, Referrers, or Recruiters
      continue outside workbrook for fee-avoidance purposes; and failing to
      report a hire or material hiring event connected to a workbrook-generated
      Candidate.
    </p>
    <p style={styles.paragraph}>
      Where workbrook reasonably determines circumvention has occurred, workbrook
      may charge the applicable placement fee or marketplace fee, deny or recover
      promotional benefits, suspend or terminate the Employer account, withhold
      services, and pursue additional remedies available under law or contract.
    </p>

    <h5 style={styles.heading}>13. Audits, Disputes, and workbrook Records</h5>
    <p style={styles.paragraph}>
      workbrook may use its records, logs, timestamps, analytics, billing data,
      traffic filters, and platform data to determine usage-based billing,
      attribution, circumvention, reward eligibility, and compliance outcomes.
    </p>
    <p style={styles.paragraph}>
      Employers agree to cooperate in good faith with reasonable requests for
      confirmation of interviews, offers, salary, Start Date, and termination
      data relevant to billing, rewards, or guarantees.
    </p>

    <h5 style={styles.heading}>14. Governing Terms</h5>
    <p style={styles.paragraph}>
      All disclaimers, liability limitations, indemnities, dispute-resolution
      terms, and miscellaneous provisions in the workbrook Terms of Use apply to
      these Employer Commercial Terms unless a separate signed agreement
      expressly states otherwise.
    </p>
  </div>
);

const ReferralTermsSection = () => (
  <div>
    <hr style={styles.sectionDivider} />
    <p style={styles.sectionLabel}>Referral Rewards</p>
    <h2 style={styles.sectionTitle}>Referral Rewards Terms</h2>

    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <p style={styles.paragraph}>
      These Referral Rewards Terms ("Referral Terms") govern Referral validity,
      attribution, reward eligibility, payout timing, fraud controls, taxes, and
      disputes for any Referral Reward or bounty program made available through
      workbrook.
    </p>
    <p style={styles.paragraph}>
      These Referral Terms are incorporated into and form part of the workbrook
      Terms of Use. In the event of conflict, these Referral Terms control for
      the reward subjects they expressly govern. Job-specific reward notices,
      campaign terms, or employer-specific reward programs may supplement these
      Referral Terms and will control to the extent they expressly vary the
      general program rules.
    </p>

    <h5 style={styles.heading}>2. Referrer Eligibility</h5>
    <p style={styles.paragraph}>
      Any eligible user may make a Referral through the Services, subject to
      these Terms and any role, geography, campaign, or job-specific restrictions
      stated by workbrook.
    </p>
    <p style={styles.paragraph}>
      By making a Referral, you represent and warrant that you are acting
      lawfully and in good faith; that you have a reasonable basis to believe the
      referred person may be relevant to the role; that you have any required
      permission or lawful basis to share the referred person's information or to
      contact them in connection with the opportunity; and that you will not
      engage in spam, mass low-quality submissions, fake referrals,
      self-referrals, or manipulation.
    </p>

    <h5 style={styles.heading}>3. When a Referral Is Credited</h5>
    <p style={styles.paragraph}>
      A Referral is credited when workbrook's systems determine that a Candidate
      was referred through your unique workbrook-tracked referral mechanism and
      the referral satisfies workbrook's attribution rules.
    </p>
    <p style={styles.paragraph}>
      A Referral may be deemed invalid if the Candidate was already in the
      Employer's active pipeline; the Candidate had already applied or been
      submitted for the role within the relevant lookback period; the referral
      cannot be verified; the referral resulted from prohibited conduct; or the
      referral is duplicated and another Referrer has valid prior timestamp
      priority.
    </p>
    <p style={styles.paragraph}>
      Unless workbrook states otherwise for a campaign or service, duplicate
      referrals are resolved on a first valid submission basis using workbrook's
      records. In the event of duplicate referrals for the exact same Candidate
      and role, the Referral Reward will be exclusively credited to the Referrer
      whose unique tracking link generated the earliest timestamped application
      or initial account creation, as recorded down to the millisecond by
      workbrook's internal server logs.
    </p>

    <h5 style={styles.heading}>4. Successful Referral and Reward Trigger</h5>
    <p style={styles.paragraph}>
      A Referral Reward is earned only if the referred Candidate becomes a
      verified Hire for a Tier 3 job, the Hire is attributable to your Referral
      under workbrook's systems and rules, any applicable waiting period,
      Guarantee Period, fraud checks, employer confirmation steps, and payment
      conditions are satisfied, and you remain in compliance with the workbrook
      documents.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise stated by workbrook, a referral is not reward-eligible
      merely because a Candidate clicks, views, applies, interviews, or receives
      an offer.
    </p>

    <h5 style={styles.heading}>5. Reward Amounts</h5>
    <p style={styles.paragraph}>
      Reward percentages, fixed amounts, role bands, payout schedules, and
      promotional reward structures may vary by job, campaign, geography, tier,
      or Employer arrangement. Where workbrook publishes a specific reward
      formula for a role or campaign, that published formula applies.
    </p>
    <p style={styles.paragraph}>
      Unless explicitly included, bonuses, equity, relocation packages, and
      non-cash benefits are excluded from salary-based reward calculations.
      workbrook may round, adjust, or recalculate rewards to reflect verified
      salary information, corrected data, currency conversion, taxes, caps,
      minimums, maximums, or employer-specific terms.
    </p>

    <h5 style={styles.heading}>6. Payout Timing and Conditions</h5>
    <p style={styles.paragraph}>
      workbrook may pay rewards in stages, including milestone-based structures
      such as partial payment after 30 days of employment and the balance after
      completion of the Guarantee Period.
    </p>
    <p style={styles.paragraph}>
      Before payout, workbrook may require identity verification, payment account
      verification, tax documentation, fraud screening, employer confirmation of
      salary, Start Date, and role details, and confirmation that the Hire
      remains in good standing through the relevant milestone date.
    </p>
    <p style={styles.paragraph}>
      workbrook may delay, withhold, reverse, reduce, or deny payout where
      fraud, abuse, collusion, or manipulation is suspected; the Employer
      disputes attribution in good faith; the Employer has not paid workbrook
      where Employer payment is a condition of the reward model; required tax or
      verification information has not been provided; the Candidate did not
      satisfy the required employment milestone; or workbrook determines the
      Referral is invalid under these Terms.
    </p>

    <h5 style={styles.heading}>7. Referrer Conduct Restrictions</h5>
    <p style={styles.paragraph}>
      Referrers may not refer themselves; offer kickbacks, side-payments, or
      reward-sharing arrangements to Candidates, hiring managers, recruiters, or
      Employer representatives for the purpose of manipulating outcomes; submit
      Candidates without authorization where such authorization is required by
      law; create multiple accounts to increase reward eligibility; or manipulate
      referral timestamps, links, identities, or platform behavior.
    </p>
    <p style={styles.paragraph}>
      workbrook may suspend referral privileges, invalidate Referrals, reverse
      rewards, or terminate accounts for any violation of this Section.
    </p>

    <h5 style={styles.heading}>8. Taxes and Compliance</h5>
    <p style={styles.paragraph}>
      Referrers are solely responsible for any taxes, duties, levies,
      withholdings, or reporting obligations arising from any Referral Reward,
      except to the extent workbrook is required by law to withhold or report
      amounts.
    </p>
    <p style={styles.paragraph}>
      workbrook may require completion of tax forms, identity verification,
      payment verification, or compliance checks before releasing a reward and
      may withhold or report amounts where required by law.
    </p>

    <h5 style={styles.heading}>9. Employer Nonpayment and Dependency Risks</h5>
    <p style={styles.paragraph}>
      Where a reward program is funded or economically dependent on an Employer
      payment obligation, workbrook may condition reward payout on workbrook's
      receipt of the corresponding Employer payment. If an Employer fails to pay,
      becomes insolvent, disputes attribution in good faith, or otherwise
      prevents completion of the funding event, workbrook may delay, reduce, or
      decline payout unless workbrook expressly agrees otherwise in writing.
    </p>
    <p style={styles.paragraph}>
      Referrers acknowledge and agree that workbrook's obligation to issue a
      Referral Reward is strictly contingent upon workbrook's actual receipt of
      the corresponding placement fee from the Employer. While workbrook will
      make commercially reasonable efforts to collect owed fees, Employer
      default, insolvency, or breach of contract may result in the delay or
      cancellation of the Referral Reward without liability to workbrook.
    </p>

    <h5 style={styles.heading}>10. Disputes and Final Determinations</h5>
    <p style={styles.paragraph}>
      workbrook may use its records, timestamps, link tracking, platform events,
      billing data, communications logs, and other platform evidence to determine
      Referral validity, duplicate priority, reward eligibility, fraud, and
      payout amount.
    </p>
    <p style={styles.paragraph}>
      In the event of a dispute about a Referral, duplicate claim, attribution,
      or reward amount, workbrook's good-faith determination based on its records
      will control unless a separate written agreement or applicable law requires
      otherwise.
    </p>

    <h5 style={styles.heading}>11. Governing Terms</h5>
    <p style={styles.paragraph}>
      All disclaimers, liability limitations, indemnities, dispute-resolution
      terms, and miscellaneous provisions in the workbrook Terms of Use apply to
      these Referral Terms unless a separate written agreement expressly states
      otherwise.
    </p>
  </div>
);

const CandidateProductTermsSection = () => (
  <div>
    <hr style={styles.sectionDivider} />
    <p style={styles.sectionLabel}>Candidate Product Terms</p>
    <h2 style={styles.sectionTitle}>Workbrook Candidate Product Terms</h2>

    <p style={styles.paragraph}>
      Terms governing candidate-facing match tools, URL scan features, peer
      insights, AI rewrite suggestions, and related credits or subscriptions.
    </p>
    <p style={styles.paragraph}>
      <strong>Prepared for:</strong> workbrook
      <br />
      <strong>Linked documents:</strong> 1) Terms of Use &nbsp;&middot;&nbsp;
      2) Employer / Marketplace Commercial Terms &nbsp;&middot;&nbsp;
      3) Referral Rewards Terms
    </p>
    <p style={styles.paragraph}>
      <strong>Effective date:</strong> [Insert Date]
    </p>
    <p style={styles.paragraph}>
      This document is intended to be used together with the other workbrook
      legal documents referenced above. In the event of conflict, these
      Candidate Product Terms control over general platform terms only for the
      subject matter they expressly govern.
    </p>
    <p style={styles.paragraph}>
      <em>Document status: final draft for legal review.</em>
    </p>

    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <p style={styles.paragraph}>
      These Candidate Product Terms ("Candidate Terms") govern candidate-facing
      match tools, premium match products, URL scan features, peer benchmarking
      features, AI-generated career assistance tools, credits, packs,
      subscriptions, and related functionality made available through workbrook.
    </p>
    <p style={styles.paragraph}>
      These Candidate Terms are incorporated into and form part of the workbrook
      Terms of Use. In the event of conflict, these Candidate Terms control for
      the candidate-facing product subjects they expressly govern. Job-specific
      notices, checkout disclosures, feature pages, promotional campaigns, or
      product-specific purchase terms may supplement these Candidate Terms and
      will control to the extent they expressly vary the general program rules.
    </p>

    <h5 style={styles.heading}>2. Covered Candidate Product Features</h5>
    <p style={styles.paragraph}>
      Candidate Product Features may include, without limitation, On-Platform
      Job Match, Any Job URL Match, Peer-Match Score, Peer Insights, AI Rewrite
      Suggestions, scan packs, subscription-based access, feature bundles, trial
      access, promotional entitlements, and related candidate-support tools made
      available through the Services.
    </p>
    <p style={styles.paragraph}>
      All Candidate Product Features remain part of the Services and are also
      subject to the workbrook Privacy Policy, any pricing or checkout terms
      presented at the time of purchase, and the disclaimers, liability
      limitations, dispute-resolution terms, and other incorporated provisions
      of the workbrook legal document set.
    </p>

    <h5 style={styles.heading}>3. On-Platform Job Match</h5>
    <p style={styles.paragraph}>
      workbrook may provide On-Platform Job Match for jobs posted, displayed,
      or otherwise made available within the workbrook platform. On-Platform
      Job Match may include match percentages, compatibility indicators,
      skill-fit signals, missing-skill highlights, ranking outputs,
      recommendations, and other relevance-based summaries.
    </p>
    <p style={styles.paragraph}>
      On-Platform Job Match is provided for informational purposes only.
      workbrook does not guarantee that any result, score, signal, or
      recommendation is accurate, complete, reflects the full criteria used by
      an employer, predicts interview selection, predicts job suitability, or
      predicts hiring outcome.
    </p>
    <p style={styles.paragraph}>
      workbrook may modify the methodology, weighting, inputs, logic, display,
      limits, or availability of On-Platform Job Match at any time.
    </p>

    <h5 style={styles.heading}>4. Any Job URL Match</h5>
    <p style={styles.paragraph}>
      workbrook may permit users to submit, upload, paste, or scan external job
      postings using third-party URLs.
    </p>
    <p style={styles.paragraph}>
      By using Any Job URL Match, you acknowledge and agree that you are
      responsible for the URL and any related data you submit; workbrook does
      not own, control, endorse, or guarantee any third-party website or job
      posting; workbrook does not guarantee that any external URL can be
      accessed, parsed, read, imported, or processed successfully; third-party
      sites may change, remove, restrict, block, or interfere with access to
      job content at any time; and workbrook may limit, reject, delay, or
      disable scans for certain domains, sources, job types, geographies, or
      technical reasons.
    </p>
    <p style={styles.paragraph}>
      workbrook is not responsible for broken links, inaccessible pages,
      parsing failures, formatting issues, removed postings, outdated content,
      or changes made by third-party sites. Your use of third-party job sites
      remains subject to those third parties' own terms and policies.
    </p>

    <h5 style={styles.heading}>5. Peer-Match Score and Peer Insights</h5>
    <p style={styles.paragraph}>
      workbrook may provide Peer-Match Score and Peer Insights to help users
      understand how their profile, resume, qualifications, or experience
      compares to relevant benchmarks.
    </p>
    <p style={styles.paragraph}>
      Peer-Match Score and Peer Insights may be based on user-submitted
      information, workbrook profile data, resume data, job-description
      content, aggregated platform information, inferred categories,
      model-generated comparison sets, or market-facing data sources selected
      by workbrook.
    </p>
    <p style={styles.paragraph}>
      By using Peer-Match Score or Peer Insights, you acknowledge and agree
      that any peer group, percentile, "top peer," "top 5%," "industry peer,"
      or similar benchmark is an estimate only; workbrook does not guarantee
      that any benchmark is exhaustive, current, or statistically
      representative; outputs may be inferred, probabilistic, incomplete,
      subjective, or incorrect; such outputs are not factual statements about
      your actual market ranking, interview chances, or employability; and
      workbrook may change the comparison methodology, peer logic, data
      inputs, thresholds, and output design at any time.
    </p>
    <p style={styles.paragraph}>
      Peer-Match Score and Peer Insights are informational decision-support
      tools only and are not employment, compensation, recruiting, legal, or
      career advice.
    </p>

    <h5 style={styles.heading}>6. Basic and Premium Insight Levels</h5>
    <p style={styles.paragraph}>
      workbrook may offer Candidate Product Features in different versions,
      tiers, or packages, including free access, limited access, trial access,
      one-time purchase packs, weekly subscriptions, monthly subscriptions,
      premium or deep-insight versions, and promotional or bundled access.
    </p>
    <p style={styles.paragraph}>
      Without limitation, workbrook may distinguish between a basic Peer-Match
      Score output, such as a simple percentage or limited summary, and a
      premium or deep-insight output, such as missing skills, competitive gaps,
      expanded analysis, or suggested improvements.
    </p>
    <p style={styles.paragraph}>
      workbrook may add, remove, rename, bundle, unbundle, limit, suspend, or
      discontinue any feature level, pricing tier, entitlement, or package at
      any time, subject to applicable law.
    </p>

    <h5 style={styles.heading}>7. AI Rewrite Suggestions</h5>
    <p style={styles.paragraph}>
      workbrook may provide AI Rewrite Suggestions to help users improve
      resumes, profiles, summaries, bullet points, application materials, or
      other career documents.
    </p>
    <p style={styles.paragraph}>
      AI Rewrite Suggestions may include rewritten bullet points, skill-based
      resume edits, phrasing suggestions, summary rewrites, keyword
      recommendations, role-tailored edits, and competitive-gap response
      suggestions.
    </p>
    <p style={styles.paragraph}>
      By using AI Rewrite Suggestions, you acknowledge and agree that all AI
      Rewrite Suggestions are automatically generated and may contain errors,
      omissions, generic language, unsuitable phrasing, or incorrect
      assumptions; you are solely responsible for reviewing, editing,
      validating, and approving any generated content before using it;
      workbrook does not guarantee that any AI Rewrite Suggestion is accurate,
      truthful, lawful, non-infringing, employer-compliant, or effective; and
      you must not use any generated content that is false, misleading,
      fabricated, defamatory, or otherwise unlawful.
    </p>
    <p style={styles.paragraph}>
      workbrook is not responsible for any outcome arising from your use,
      non-use, or submission of AI Rewrite Suggestions.
    </p>

    <h5 style={styles.heading}>8. Credits, Packs, and Subscriptions</h5>
    <p style={styles.paragraph}>
      Candidate Product Features may be offered through free access, paid
      Credits, one-time packs, weekly subscriptions, monthly subscriptions,
      promotional offers, or other purchase models presented through the
      Services.
    </p>
    <p style={styles.paragraph}>
      Unless workbrook expressly states otherwise, Credits and feature
      entitlements are non-transferable; promotional Credits have no cash
      value; unused Credits may expire if an expiration period is disclosed at
      the time of purchase or grant; scan packs, premium feature unlocks, and
      similar entitlements are non-refundable once used, activated, consumed,
      or made available, except where required by law; subscriptions may
      automatically renew if disclosed at checkout; you authorize recurring
      charges where applicable based on the purchase terms shown at checkout;
      and workbrook may impose fair-use limits, anti-abuse thresholds, scan
      caps, rate limits, and technical restrictions.
    </p>
    <p style={styles.paragraph}>
      workbrook may distinguish between purchased Credits and promotional
      Credits for purposes of redemption order, expiration, eligibility, or
      refund treatment.
    </p>

    <h5 style={styles.heading}>9. No Guarantee of Interview, Offer, or Hire</h5>
    <p style={styles.paragraph}>
      Candidate Product Features are intended to support users in their job
      search, but they do not guarantee any result.
    </p>
    <p style={styles.paragraph}>
      Use of Candidate Product Features does not guarantee job matches, better
      applications, employer responses, interviews, offers, hires, improved
      competitiveness, or improved career outcomes. All hiring decisions are
      made by employers or their representatives, not by workbrook.
    </p>

    <h5 style={styles.heading}>10. Beta Features and Feature Availability</h5>
    <p style={styles.paragraph}>
      Some Candidate Product Features may be designated or operated as beta,
      pilot, preview, early-access, experimental, or limited-release features.
    </p>
    <p style={styles.paragraph}>
      Such features may be incomplete, unstable, unavailable, changed
      frequently, or withdrawn without notice. workbrook may modify, suspend,
      restrict, replace, or discontinue any Candidate Product Feature, pricing
      model, package, score logic, or feature availability at any time, with
      or without notice, unless required otherwise by law.
    </p>
    <p style={styles.paragraph}>
      Candidate Product Features are provided on an "as is" and "as available"
      basis, and workbrook has no obligation to maintain, support, correct, or
      continue any specific Candidate Product Feature.
    </p>

    <h5 style={styles.heading}>11. Fraud, Abuse, and Misuse</h5>
    <p style={styles.paragraph}>
      workbrook may suspend, restrict, or terminate access to Candidate Product
      Features, invalidate Credits, cancel subscriptions, or deny feature
      access where workbrook reasonably suspects fraud, account sharing,
      automated misuse, scraping, payment abuse, chargebacks, manipulation of
      scans, scores, or feature outputs, or use inconsistent with the Terms of
      Use or these Candidate Terms.
    </p>
    <p style={styles.paragraph}>
      workbrook may also reverse promotional access, deny refunds, or limit
      future purchases where misuse is detected.
    </p>

    <h5 style={styles.heading}>12. External Content Disclaimer</h5>
    <p style={styles.paragraph}>
      Where Candidate Product Features reference, interact with, or process
      third-party content, including job boards, employer career pages, or
      external sources, workbrook makes no representation or warranty
      regarding authorization, ownership, legality, availability, continuity,
      formatting, completeness, accuracy, or timeliness.
    </p>
    <p style={styles.paragraph}>
      workbrook is not responsible for third-party content or third-party site
      behavior.
    </p>

    <h5 style={styles.heading}>13. Candidate Responsibility</h5>
    <p style={styles.paragraph}>
      You are solely responsible for the accuracy of the information you
      provide, the final content of your resume, profile, and applications,
      ensuring that any submitted material is truthful and lawful, and
      determining whether to rely on any Candidate Product Feature.
    </p>
    <p style={styles.paragraph}>
      You remain responsible for your professional judgment, job-search
      decisions, and application strategy.
    </p>

    <h5 style={styles.heading}>14. Governing Terms</h5>
    <p style={styles.paragraph}>
      All disclaimers, liability limitations, indemnities, dispute-resolution
      terms, and miscellaneous provisions in the workbrook Terms of Use apply
      to these Candidate Terms unless a separate written agreement expressly
      states otherwise.
    </p>
  </div>
);

const TermsAndConditions = () => {
  const articleRef = useRef(null);
  const topRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [showTop, setShowTop] = useState(false);

  // Walk rendered article to build a grouped TOC and assign stable ids to h5s.
  useEffect(() => {
    if (!articleRef.current) return undefined;
    const root = articleRef.current;
    const nodes = root.querySelectorAll("h2, h5");
    const groups = [];
    let current = null;
    const used = new Set();
    nodes.forEach((el, i) => {
      if (el.tagName === "H2") {
        current = { groupTitle: el.textContent.trim(), items: [] };
        const base = slugify(el.textContent, "group-" + i);
        let id = base;
        let n = 1;
        while (used.has(id)) id = base + "-" + ++n;
        used.add(id);
        el.id = id;
        current.groupId = id;
        groups.push(current);
      } else if (el.tagName === "H5" && current) {
        const base = slugify(el.textContent, "s-" + i);
        let id = base;
        let n = 1;
        while (used.has(id)) id = base + "-" + ++n;
        used.add(id);
        el.id = id;
        current.items.push({ id, heading: el.textContent.trim() });
      }
    });
    setToc(groups);

    const targets = root.querySelectorAll("h5[id]");
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0] && visible[0].target && visible[0].target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 360);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const effectiveAt = "2026-01-01";
  const updatedAt = "2026-04-01";

  const scrollToTop = () => {
    if (topRef.current && topRef.current.scrollIntoView) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main className="wb-legal" ref={topRef}>
      <style>{LEGAL_CSS}</style>

      {/* Hero */}
      <header className="wb-legal__hero">
        <div className="wb-legal__hero-orb wb-legal__hero-orb--a" aria-hidden="true" />
        <div className="wb-legal__hero-orb wb-legal__hero-orb--b" aria-hidden="true" />
        <div className="wb-legal__hero-inner">
          <span className="wb-legal__eyebrow">
            <span className="wb-legal__eyebrow-dot" />
            Legal
          </span>
          <h1 className="wb-legal__title">Terms &amp; Conditions</h1>
          <p className="wb-legal__lede">
            These Terms explain the rules for using Workbrook — what you can expect
            from us and what we need from you. Please read them carefully.
          </p>
        
        </div>
      </header>

      {/* Body */}
      <div className="wb-legal__wrap">
        <div className="wb-legal__grid">
          <aside className="wb-legal__toc" aria-label="Table of contents">
            <div className="wb-legal__toc-inner">
              <div className="wb-legal__toc-title">On this page</div>
              {toc.map((g) => (
                <div key={g.groupId} className="wb-legal__toc-group">
                  <a href={"#" + g.groupId} className="wb-legal__toc-group-label">
                    {g.groupTitle}
                  </a>
                  <ul className="wb-legal__toc-list">
                    {g.items.map((s) => (
                      <li key={s.id}>
                        <a
                          href={"#" + s.id}
                          className={
                            "wb-legal__toc-link" +
                            (activeId === s.id ? " wb-legal__toc-link--active" : "")
                          }
                        >
                          <span className="wb-legal__toc-marker" />
                          <span>{s.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          <article className="wb-legal__article" ref={articleRef}>
            <TermsOfUseSection />
            <EmployerTermsSection />
            <ReferralTermsSection />
            <CandidateProductTermsSection />

            <div className="wb-legal__contact">
              <strong>Need to speak with legal?</strong> Email{" "}
              <a href="mailto:info@workbrook.com">info@workbrook.com</a> and
              we&apos;ll get back to you shortly.
            </div>
          </article>
        </div>
      </div>

      {/* Back to top */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        className="wb-legal__top"
        data-show={showTop ? "true" : undefined}
      >
        ↑
      </button>
    </main>
  );
};

export default TermsAndConditions;
